import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, History, PlayArrow } from "@mui/icons-material";
import {
  useCreateSchedule,
  useDeleteSchedule,
  useScheduleFires,
  useSchedules,
  useUpdateSchedule,
} from "@/hooks/useSchedules";
import type { Schedule, ScheduleCreate, ScheduleFireStatus, ScheduleUpdate } from "@/types";

// Common cron presets — covers ~95% of what Steve + Evelina will set up. The
// raw input still accepts any croniter-valid 5-field expression so power
// users aren't boxed in.
const CRON_PRESETS = [
  { label: "Weekdays 0700 Europe/London", value: "0 6 * * 1-5" },
  { label: "Daily 0700 Europe/London", value: "0 6 * * *" },
  { label: "Monday 0900 Europe/London", value: "0 8 * * 1" },
  { label: "Every hour, 0800-1800", value: "0 7-17 * * *" },
];

const TIMEZONES = ["Europe/London", "UTC"];

function formatStatus(status: ScheduleFireStatus): {
  label: string;
  color: "success" | "error" | "warning" | "default";
} {
  switch (status) {
    case "succeeded":
      return { label: "succeeded", color: "success" };
    case "failed":
      return { label: "failed", color: "error" };
    case "skipped_concurrent":
      return { label: "skipped (concurrent)", color: "warning" };
    case "skipped_inactive":
      return { label: "skipped (inactive)", color: "default" };
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export default function Schedules() {
  const { data: schedules, isLoading } = useSchedules();
  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();
  const deleteMutation = useDeleteSchedule();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ScheduleCreate>({
    name: "",
    cron_expression: CRON_PRESETS[0].value,
    prompt: "",
    timezone: "Europe/London",
    is_active: true,
  });
  const [firesViewerId, setFiresViewerId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const openNew = () => {
    setEditingId(null);
    setForm({
      name: "",
      cron_expression: CRON_PRESETS[0].value,
      prompt: "",
      timezone: "Europe/London",
      is_active: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (s: Schedule) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      cron_expression: s.cron_expression,
      prompt: s.prompt,
      timezone: s.timezone,
      is_active: s.is_active,
    });
    setEditorOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        const body: ScheduleUpdate = { ...form };
        await updateMutation.mutateAsync({ id: editingId, body });
      } else {
        await createMutation.mutateAsync(form);
      }
      setEditorOpen(false);
      setSnackbar({
        open: true,
        message: editingId ? "Schedule updated" : "Schedule created",
        severity: "success",
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  const toggleActive = async (s: Schedule) => {
    try {
      await updateMutation.mutateAsync({ id: s.id, body: { is_active: !s.is_active } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to toggle";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  const remove = async (s: Schedule) => {
    if (!confirm(`Delete schedule "${s.name}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync(s.id);
      setSnackbar({ open: true, message: "Schedule deleted", severity: "success" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to delete";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Scheduled Prompts
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openNew}>
          New schedule
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define cron-style prompts your personal assistant runs automatically. System schedules
        (owned by Claude) are read-only and shown for visibility — you can't edit them, but you can
        see when they last fired.
      </Typography>

      <Stack spacing={2}>
        {schedules?.map((s) => (
          <Card key={s.id} variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                      {s.name}
                    </Typography>
                    {s.is_system && (
                      <Chip label="System" size="small" color="secondary" variant="outlined" />
                    )}
                    {!s.is_active && <Chip label="Inactive" size="small" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    <code>{s.cron_expression}</code> · {s.timezone}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, fontSize: "0.85rem" }}
                  >
                    Next: {formatRelative(s.next_fire_at)} · Last: {formatRelative(s.last_fired_at)}
                  </Typography>
                  {s.prompt && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        fontStyle: "italic",
                        color: "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      "{s.prompt}"
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Recent fires">
                    <IconButton onClick={() => setFiresViewerId(s.id)} size="small">
                      <History fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {!s.is_system && (
                    <>
                      <Tooltip title={s.is_active ? "Pause" : "Resume"}>
                        <Switch
                          size="small"
                          checked={s.is_active}
                          onChange={() => toggleActive(s)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEdit(s)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => remove(s)} size="small" color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
        {schedules?.length === 0 && (
          <Alert severity="info">
            No schedules yet. Click "New schedule" to set up your first automated prompt.
          </Alert>
        )}
      </Stack>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit schedule" : "New schedule"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Monday pipeline review"
              fullWidth
              autoFocus
            />
            <TextField
              label="When to run (cron)"
              value={form.cron_expression}
              onChange={(e) => setForm((f) => ({ ...f, cron_expression: e.target.value }))}
              helperText="5-field cron (minute hour day-of-month month day-of-week). Pick a preset below or write your own."
              fullWidth
            />
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {CRON_PRESETS.map((p) => (
                <Chip
                  key={p.value}
                  label={p.label}
                  size="small"
                  variant={form.cron_expression === p.value ? "filled" : "outlined"}
                  onClick={() => setForm((f) => ({ ...f, cron_expression: p.value }))}
                  clickable
                />
              ))}
            </Stack>
            <TextField
              label="Timezone"
              select
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              fullWidth
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prompt"
              value={form.prompt}
              onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
              placeholder="What should the assistant do when this fires? e.g. 'Summarise this week's pipeline movement and flag anything stuck.'"
              multiline
              minRows={4}
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={form.is_active ?? true}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              <Typography variant="body2">Active</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!form.name.trim() || !form.cron_expression.trim() || !form.prompt.trim()}
          >
            {editingId ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <FiresViewer
        scheduleId={firesViewerId}
        schedule={schedules?.find((s) => s.id === firesViewerId) ?? null}
        onClose={() => setFiresViewerId(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface FiresViewerProps {
  scheduleId: string | null;
  schedule: Schedule | null;
  onClose: () => void;
}

function FiresViewer({ scheduleId, schedule, onClose }: FiresViewerProps) {
  const { data: fires, isLoading } = useScheduleFires(scheduleId ?? undefined, 10);
  return (
    <Dialog open={!!scheduleId} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Recent fires
        {schedule && (
          <Typography variant="body2" color="text.secondary">
            {schedule.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : fires?.length === 0 ? (
          <Alert severity="info">
            No fires yet. The first run will be at {formatRelative(schedule?.next_fire_at ?? null)}.
          </Alert>
        ) : (
          <Stack spacing={1}>
            {fires?.map((f) => {
              const s = formatStatus(f.status);
              return (
                <Card key={f.id} variant="outlined">
                  <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip label={s.label} color={s.color} size="small" />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {formatRelative(f.fired_at)}
                      </Typography>
                      {f.conversation_id && (
                        <Tooltip title="Open conversation">
                          <IconButton
                            size="small"
                            component="a"
                            href={`/my-assistant?conversation=${f.conversation_id}`}
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {f.output_summary && (
                      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                        {f.output_summary}
                      </Typography>
                    )}
                    {f.error_message && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: "error.main",
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                        }}
                      >
                        {f.error_message}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
